import React from "react";
import { Alert, Box, Grid, Tooltip, Typography, useTheme } from "@mui/material";
import moment from "moment";
import { 
	ArrowForwardRounded, 
	InfoOutlined, 
	ShoppingBagOutlined, 
	ThumbDownAltOutlined, 
	ThumbUpAltOutlined,
	ThumbDownAltRounded,
	ThumbUpAltRounded 
} from "@mui/icons-material";
import PriceFormater from "../PriceFormater";
import { CTAButton } from "../home/blocks/odyssee/OdysseeCard";
import ChangeQuantityComponent from "../basket/ChangeQuantityComponent";

import { ReactComponent as TicketMiddleIcon } from "../../resources/images/svg/event/ticketMiddle.svg";
import { ReactComponent as Level1Icon } from "../../resources/images/svg/event/level1.svg";
import { ReactComponent as Level2Icon } from "../../resources/images/svg/event/level2.svg";

import InexCard from "../InexCard";

import backgroundImage from "../../resources/images/png/events/img.png";
import { useLazyGetProductQuery } from "../../store/api/product";
import { useSelector } from "react-redux";
import { getUser } from "../../store/reducers/authSlice";
import { calculateProductPrice } from "../basket/BasketProductCard";
import EventReductionsDetailDialog from "./EventReductionsDetailDialog";
import { useAuthToken, useIsPraticien } from "../../hooks/auth";
import { useGetProInformationQuery } from "../../store/api/user";
import { useUpdateVoteMutation, useLazyGetVoteQuery } from "../../store/api/event";

const EventDate = ({ event, date }) => {
	const theme = useTheme();

	const authToken = useAuthToken();

	const [isVote, setIsVote] = React.useState(null);

	const date1 = React.useMemo(() => {
		if (date) return moment.unix(date.dateDebut);
		return null;
	}, [date]);

	const date2 = React.useMemo(() => {
		if (date) return moment.unix(date.dateFin);
		return null;
	}, [date]);

	const isSameDay = React.useMemo(() => {
		return date1 && date2 && date1?.format("LL") === date2?.format("LL");
	}, [date1, date2]);

	const isAfterNext12Hours = React.useMemo(() => {
		if (!date1) return false;
		const in12Hours = moment().add(12, 'hours');
		return date1.isAfter(in12Hours);
	}, [date1]);

	const [quantity, setQuantity] = React.useState(1);

	const hasProduct = React.useMemo(() => {
		return date?.idproduit && date?.idproduit !== 0;
	}, [date]);

	const [getProductQuery] = useLazyGetProductQuery({ id: date.idproduit });
	const [product, setProduct] = React.useState(null);

	React.useEffect(() => {
		if (hasProduct && date) {
			getProductQuery({ id: date.idproduit }).then((result) => {
				if (!result.isError && result.data?.product) setProduct(result.data.product);
			});
		}
	}, [date, hasProduct]);

	const maxPromo = React.useMemo(() => {
		const idx = ["reduct_tv", "reduct_mag"];
		return idx.reduce((acc, key) => acc + (product?.[key] ?? 0), 0);
	}, [product]);

	const user = useSelector(getUser);

	const [totalPrice, totalPromo, totalPriceBeforePromo] = React.useMemo(() => {
		if (!product) return [0, 0, 0];
		return calculateProductPrice(product, user);
	}, [product, user]);

	const [showDialog, setShowDialog] = React.useState(false);

	const place = React.useMemo(() => {
		return date?.lieux;
	}, [date]);

	const addressUrl = React.useMemo(() => {
		if (!place) return "#";
		return `https://www.google.com/maps/place/${place.adresse}+${place.ville}+${place.cp}+${place.pays}`.replaceAll(
			" ",
			"+",
		);
	}, [place]);

	const hasPromo = React.useMemo(() => {
		return maxPromo > (totalPriceBeforePromo - totalPrice) * 100;
	}, [totalPrice]);

	const isPraticien = useIsPraticien();

	const canAddToBasket = React.useMemo(() => {
		return isAfterNext12Hours && hasProduct && product && isPraticien && product?.stock - product?.vendu > 0;
	}, [hasProduct, product, isPraticien]);

	const { data } = useGetProInformationQuery({ authToken }, { skip: !authToken });

	const praticienLevel = React.useMemo(() => {
		return data?.niveau ?? 0;
	}, [data]);

	// On extrait 'data' et 'isSuccess' directement
	const [getVoteQuery, { data: voteData, isSuccess }] = useLazyGetVoteQuery();

	React.useEffect(() => {
		if (authToken && event?.id) {
			getVoteQuery({ id: event.id, authToken });
		}
	}, [authToken, event, getVoteQuery]);

	React.useEffect(() => {
		if (isSuccess && voteData) {
			setIsVote(voteData.vote === 2);
		}
	}, [isSuccess, voteData]);

	const [updateVoteMutation] = useUpdateVoteMutation();

  	const handleValidate = async (vote) => {
		try {
		// 2. On déclenche la mutation
		// .unwrap() permet de tomber dans le catch en cas d'erreur (403, 504, etc.)
		await updateVoteMutation({
			id: event.id,
			vote,
			authToken
		}).unwrap();

		} catch (err) {
		// 4. Gestion des erreurs Cloud Run
		console.error("Échec de la mutation :", err);
		}
	};

	if (!event || !date) return null;

	return (
		<Grid
			item
			container
			mt={3}
			mb={3}
			xs={12}
			sx={{
				display: "flex",
				flexDirection: "row",
				alignItems: "center",
				justifyContent: "center",
				gap: 5,
			}}
		>
			<Grid
				item
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
				}}
			>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						borderRadius: "30px",
						filter: "drop-shadow(0 0 20px #162A414A)",
					}}
				>
					<InexCard
						sx={{
							maxWidth: 240,
							paddingTop: 1.5,
							paddingBottom: 1.5,
							padding: 1.5,
							borderTopLeftRadius: "30px",
							borderTopRightRadius: "30px",
							overflow: "hidden",
							boxShadow: "none",
						}}
					>
						<img
							src={
								hasProduct && product
									? `https://medias.inrees.com/img/produit/moyen_${product.id}.jpg`
									: backgroundImage
							}
							alt={"eventImage"}
							style={{
								objectFit: "contain",
								width: "100%",
								borderTopLeftRadius: "20px",
								borderTopRightRadius: "20px",
							}}
						/>
					</InexCard>
					<TicketMiddleIcon />
					<InexCard
						sx={{
							maxWidth: 240,
							paddingTop: 1,
							paddingBottom: 2.5,
							padding: 2.5,
							borderBottomLeftRadius: "30px",
							borderBottomRightRadius: "30px",
							overflow: "hidden",
							boxShadow: "none",
						}}
					>
						<Typography
							sx={{
								fontFamily: "Branding Medium",
								fontSize: "14px",
							}}
						>
							{event?.categorie?.label}
						</Typography>
						<Typography
							sx={{
								fontFamily: "Yesevaone",
								fontSize: "21px",
								lineHeight: "1.4em",
								marginBottom: (theme) => theme.spacing(0.5),
								display: { xs: 'none', sm: 'block' }
							}}
						>
							{event?.titre}
						</Typography>
					</InexCard>
				</Box>
			</Grid>
			<Grid 
				item
				sx={{
					maxWidth: "75%",
				}}
			>
				<Typography
					sx={{
						fontFamily: "Yesevaone",
						fontSize: "37px",
					}}
				>
					{event.titre}
				</Typography>
				{place.visio === true && (
					<Typography
						sx={{
							fontFamily: "Branding Medium",
						}}
					>
						{!isSameDay
							? `En visio du ${date1.format("LL")} au ${date2.format("LL")}`
							: `En visio le ${date1.format("LL")}, de ${date1.format("HH[h]mm")} à ${date2.format("HH[h]mm")}`}
					</Typography>
				)}
				{place.visio !== true && place.adresse !== "" && (
					<Typography
						sx={{
							fontFamily: "Branding Medium",
						}}
					>
						<a
							href={addressUrl}
							target={"_blank"}
							style={{
								color: theme.palette.primary.main,
								textDecoration: "none",
								fontFamily: "Branding SemiBold",
							}}
						>{`${place.adresse}, ${place.ville}, ${place.cp} ${place.pays}`}</a>
						<br />
						{!isSameDay
							? `du ${date1.format("LL")} au ${date2.format("LL")}`
							: `le ${date1.format("LL")}, de ${date1.format("HH[h]mm")} à ${date2.format("HH[h]mm")}`}
					</Typography>
				)}
				{hasProduct && product && (
					<Box
						sx={{
							display: "flex",
							flexDirection: "row",
							alignItems: "flex-end",
						}}
					>
						<Tooltip title={totalPromo > 0 ? "Réduction octroyée grâce à vos abonnements inexploré" : ""}>
							<PriceFormater
								priceProps={{
									sx: {
										fontFamily: "Branding Bold",
										fontWeight: 700,
										textDecoration: totalPromo > 0 ? "line-through" : undefined,
										fontSize: totalPromo > 0 ? "30px" : "41px",
										color: (theme) => (totalPromo > 0 ? theme.palette.primary.main : undefined),
									},
								}}
								withTitle={false}
								withTimeFrame={false}
								price={totalPriceBeforePromo * 100}
							/>
						</Tooltip>
						{totalPromo > 0 && (
							<PriceFormater
								priceProps={{
									sx: { fontSize: 41, fontFamily: "Branding Bold", fontWeight: 700, marginLeft: 1 },
								}}
								withTitle={false}
								withTimeFrame={false}
								price={totalPrice * 100}
							/>
						)}
					</Box>
				)}
				{maxPromo > 0 && (
					<Box mt={1}>
						<Alert
							severity={"info"}
							icon={
								hasPromo && (
									<InfoOutlined
										sx={{
											color: (theme) => theme.palette.secondary.main,
											marginLeft: 1,
											marginRight: 1,
										}}
									/>
								)
							}
							sx={{
								backgroundColor: "rgba(109,53,144,0.1)",
								padding: 2,
							}}
						>
							<Typography>
								{hasPromo
									? `Bénéficiez de ${(maxPromo / 100 - totalPromo).toFixed(2).replace(".", ",")} € de réduction supplémentaire, `
									: ` Vous bénéficiez du meilleur prix grâce à vos abonnements`}

								{hasPromo && (
									<Typography
										href={"#"}
										variant={"string"}
										sx={{
											color: theme.palette.primary.main,
											textDecoration: "underline",
											"&:hover": {
												cursor: "pointer",
											},
										}}
										onClick={() => {
											setShowDialog(true);
										}}
									>
										{`en cliquant ici !`}
									</Typography>
								)}
							</Typography>
						</Alert>
						<EventReductionsDetailDialog
							open={showDialog}
							onClose={() => {
								setShowDialog(false);
							}}
							product={product}
						/>
					</Box>
				)}
				{/*canAddToBasket && praticienLevel > 1 && (
					<Box
						mt={2}
						sx={{
							display: "flex",
						}}
					>
						<ChangeQuantityComponent
							quantity={quantity}
							stockInformation={null}
							onPlusClicked={() => {
								if (quantity <= date.qteMax) setQuantity(quantity + 1);
							}}
							onMinusClicked={() => {
								if (quantity > 1) setQuantity(quantity - 1);
							}}
						/>
					</Box>
				)*/}
				<Typography
						sx={{
							fontFamily: "Branding Bold",
							color: theme.palette.primary.main,
							marginBottom: 1
						}}
					>
						{date?.infoPrix}
					</Typography>
				{isAfterNext12Hours && (product?.stock - product?.vendu) > 0 && (
					<Typography
						sx={{
							fontFamily: "Branding Medium",
							marginBottom: 1
						}}
					>
						
						{`⏳  ${(product?.stock - product?.vendu)} ${(product?.stock - product?.vendu > 1) ? 'places restantes' : 'place restante'} seulement – Ne laissez pas passer votre chance !`}
					</Typography>
				)}
				<Typography
						sx={{
							fontFamily: "Branding Medium",
							color: theme.palette.primary.main,
							marginBottom: 1
						}}
				> Cet évènement est réservé aux abonné(e)s Inexploré Praticiens.
				</Typography>
				<Box mt={1}>
					{praticienLevel > 1 || !canAddToBasket ? (
						<a href={canAddToBasket && product?.stock-product?.vendu > 0 ? `/panier?id=${date.idproduit}&quantity=${quantity}` : "https://praticiens.inexplore.com"}>
							<CTAButton
								variant={"texticon"}
								icon={
									<ShoppingBagOutlined
										sx={{
											fontSize: "26px",
											color: (theme) =>
												canAddToBasket || !isPraticien ? theme.palette.secondary.main : theme.palette.grey[300],
										}}
									/>
								}
								textWithGradient={false}
								disableBackground={true}
								text={
									!isPraticien
										? `${(product?.stock - product?.vendu > 0) && isAfterNext12Hours ? "Je m'abonne" : "Complet !"}`
										: canAddToBasket ? "Ajouter au panier" : "Complet !"
								}
								disabled={product?.stock <= product?.vendu }
								sx={{
									padding: 3,
									marginRight: 2,
									flexDirection: "row-reverse",
									color: (theme) => theme.palette.secondary.main,
									backgroundColor: (theme) =>
										canAddToBasket || (!isPraticien && (product?.stock > product?.vendu)) ? theme.palette.yellow : theme.palette.grey[500],
									"& > p": {
										backgroundColor: "transparent",
										textTransform: "initial",
										color: (theme) => (canAddToBasket || (!isPraticien && (product?.stock > product?.vendu)) ? undefined : theme.palette.grey[300]),
									},
								}}
							/>
						</a>
					) : (
						<Box
							sx={{
								display: "flex",
								flexDirection: "row",
								alignItems: "center",
								justifyContent: "center",
								marginTop: 2,
								padding: 1,
								backgroundColor: (theme) => theme.palette.grey[300],
								borderRadius: "25px",
								height: "50px",
							}}
						>
							<Level1Icon
								style={{
									width: "31px",
									height: "31px",
									marginRight: "5px",
								}}
							/>
							<ArrowForwardRounded
								sx={{
									marginRight: "5px",
								}}
							/>
							<Level2Icon
								style={{
									width: "31px",
									height: "31px",
									marginRight: "12px",
								}}
							/>
							<Typography
								sx={{
									color: (theme) => theme.palette.primary.main,
									fontWeight: "900",
									fontSize: "16px",
									cursor: "pointer"
								}}
								onClick={() => {
									window.open("https://praticiens.inexplore.com/", "_blank", "noopener,noreferrer");
								}}
							>
								Accédez au palier 2 pour vous inscrire
							</Typography>
						</Box>
					)}
				</Box>
				{authToken && (<Box mt={1}>

						<Grid
							severity={"info"}
							sx={{
								padding: 2,
								alignItems: "center",
								display: "flex",
								justifyContent: "space-evenly",
								maxWidth: "500px"
							}}
						>
							<Typography>{`Êtes-vous intéressé(e) par d’autres dates ?`}</Typography>
							{isVote !== null ?
							 (<>
							 	{isVote === false ? 
									(
										<ThumbDownAltRounded
											sx={{
												color: (theme) => theme.palette.secondary.main,
												marginLeft: 1,
												marginRight: 1,
											}}
										/>
									) : 
									(
										<ThumbUpAltRounded
											sx={{
												color: (theme) => theme.palette.secondary.main,
												marginLeft: 1,
												marginRight: 1,
											}}
										/>
									)
								}
								
							</>) : (
								<Grid>
									<ThumbUpAltOutlined
										sx={{
											color: (theme) => theme.palette.secondary.main,
											marginLeft: 1,
											marginRight: 1,
											"&:hover": {
												cursor: "pointer",
												color: (theme) => theme.palette.primary.main,
											},
										}}
										onClick={() => {
											setIsVote(true);
											handleValidate(2);
										}}
									/>

									<ThumbDownAltOutlined
										sx={{
											color: (theme) => theme.palette.secondary.main,
											marginLeft: 1,
											marginRight: 1,
											"&:hover": {
												cursor: "pointer",
												color: (theme) => theme.palette.primary.main,
											},
										}}
										onClick={() => {
											setIsVote(false);
											handleValidate(1);
										}}
									/>
								
								</Grid>
							 )}
							
						</Grid>
				</Box>)}
			</Grid>
		</Grid>
	);
};

export default EventDate;
